import React, { useMemo, useState } from 'react';

/**
 * Lightweight sortable table for report rows.
 *
 * Props:
 *   - headers: ordered list of column keys to show
 *   - rows: array of objects
 *   - columnLabels: optional { key: 'Pretty Label' } overrides
 *   - formatters: optional { key: (value, row) => ReactNode }
 *   - emptyMessage
 */
export default function ReportTable({
  headers,
  rows = [],
  columnLabels = {},
  columnTooltips = {},
  formatters = {},
  emptyMessage = 'No data for the selected range.',
}) {
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const sorted = useMemo(() => {
    if (!sortBy) return rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const an = Number(av), bn = Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) return (an - bn) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sortBy, sortDir]);

  const onHeaderClick = (h) => {
    if (sortBy === h) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(h); setSortDir('desc');
    }
  };

  if (!rows.length) {
    return (
      <div className="text-sm text-surface-muted italic p-4 border border-dashed border-surface-border dark:border-surface-dark-border rounded-thin">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-thin border border-surface-border dark:border-surface-dark-border">
      <table className="min-w-full text-sm">
        <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                onClick={() => onHeaderClick(h)}
                title={columnTooltips[h] || undefined}
                className="px-3 py-2 text-left font-semibold cursor-pointer select-none whitespace-nowrap hover:bg-surface-border/30"
              >
                {columnLabels[h] || h.replace(/_/g, ' ')}
                {sortBy === h && (
                  <span className="ml-1 opacity-60">{sortDir === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr
              key={i}
              className={
                i % 2
                  ? 'bg-surface-base/50 dark:bg-surface-dark-base/30'
                  : 'bg-transparent'
              }
            >
              {headers.map((h) => {
                const v = row[h];
                const fmt = formatters[h];
                return (
                  <td key={h} className="px-3 py-1.5 whitespace-nowrap align-top">
                    {fmt ? fmt(v, row) : (v == null ? '—' : String(v))}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
